import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box, Typography,
  Snackbar, Alert, TablePagination,
} from '@mui/material';
import {
  CardWrapper, SectionTitle, ButtonOutlined, Field, formatBRL,
} from '../../UI';
import * as C from '../../../theme/colors.constants';
import TableUtils from '../../../../helpers/utils/table.utils';
import { hasUserPermissions } from '../../../../helpers/utils/permissoes';
import { openPagePDF } from '../../../../helpers/utils/utils';
import {
  simularQuitacaoService, buscarAcompanhamentoDoProcessoService,
} from '../../../../services/core/processos/processosService';
import { imprimirMemoriaDeCalculo } from '../../../../services/core/processos/processosTitulosService';
import DialogSimularQuitacao from './DialogSimularQuitacao';
import BotaoNovoAcordo from '../NovoAcordo/BotaoNovoAcordo';
import TableAntecedentesProcesso from '../Antecedentes/TableAntecedentesProcesso';
import ResumoAntecedenteAtual from '../Antecedentes/ResumoAntecedenteAtual';
import DialogResumoAntecedente from '../Antecedentes/DialogResumoAntecedente';
import TableTitulosProcesso from '../Titulos/TableTitulosProcesso';
import DialogTitulo from '../Titulos/DialogTitulo';
import DialogExcluirTitulo from '../Titulos/DialogExcluirTitulo';
import DialogExpandirTitulos from '../Titulos/DialogExpandirTitulos';
import { OPCOES_TAMANHO_PAGINA_TITULOS } from '../Titulos/titulosController';
import { tiposConsultasAntecedentesTypes } from '../../../../types/tiposConsultasAntecedentesTypes';

const MENSAGEM_ERRO_SIMULAR_QUITACAO = 'Não foi possível simular a quitação.';
const MENSAGEM_ERRO_RESUMO_ANTECEDENTE = 'Não foi possível buscar o resumo deste processo.';
const MENSAGEM_ERRO_DEMONSTRATIVO_VALORES = 'Não foi possível gerar o Demonstrativo de Valores.';
// Permissão de gerenciamento de Títulos (Novo/Editar/Excluir) — mesmo código
// `ROLE_1202` que a tela real lê de `localStorage.userData.permissoes`
// (`Titulos.jsx`, `disabledGerenciarTitulos`). O protótipo usa
// `hasUserPermissions()` (lê `state.authentication.userInformation.permissoes`
// via Redux) por consistência com o resto do módulo (Unificar/Juntar,
// AW2-1033) — decisão do plano de arquitetura, a validar em uso real se as 2
// fontes de permissão (localStorage vs. Redux) sempre coincidem.
const PERMISSAO_GERENCIAR_TITULOS = 1202;
const ESTADO_INICIAL_DIALOG_TITULO = { aberto: false, titulo: null, isEditar: false };

/**
 * Aba Acompanhamento.
 *
 * Três blocos, como no print `grid_icons_check.png`:
 *   1. Cálculo do Processo — campos com os sinais (+)/(-)/(=) à direita
 *   2. Processos Antecedentes — tabela + total
 *   3. Títulos do processo — tabela larga, abaixo dos dois primeiros
 *
 * O painel "Cálculo do Processo" usa dado real (`dadosProcesso`, buscado por
 * `ControleProcessosPage.jsx` via `buscarAcompanhamentoDoProcessoService` —
 * mesmo endpoint da tela real `Acompanhamento.jsx`). `id` de cada linha é o
 * próprio nome do campo no DTO real, na ordem exigida pelo Jira AC (AW2-1032):
 * Valor Principal, Saldo Principal, Protesto, Multa, Subtotal, Juros/Despesas,
 * Honorários, Desconto, Total — sem "Renegociado" (nunca existiu na tela
 * real, era um campo do mock sem correspondente).
 *
 * Antecedentes/Títulos do processo continuam mockados (fora de escopo desta
 * etapa).
 */
const LINHAS_CALCULO = [
  { id: 'valorTotalOriginal', label: 'Valor Principal:', sinal: '(+)' },
  { id: 'valorPrincipalProcesso', label: 'Saldo Principal:', sinal: '(+)' },
  { id: 'valorProtestoProcesso', label: 'Protesto:', sinal: '(+)' },
  { id: 'valorMultaProcesso', label: 'Multa:', sinal: '(+)' },
  {
    id: 'subTotal', label: 'SUBTOTAL:', sinal: '(=)', destaque: true,
  },
  { id: 'valorJurosDespesasProcesso', label: 'Juros/Despesas:', sinal: '(+)' },
  { id: 'valorHonorarioProcesso', label: 'Honorários:', sinal: '(+)' },
  { id: 'valorDescontoProcesso', label: 'Desconto:', sinal: '(-)' },
  {
    id: 'valorTotalProcesso', label: 'TOTAL:', sinal: '(=)', destaque: true,
  },
];

const OPCOES_TAMANHO_PAGINA_ANTECEDENTES = [10, 15, 20];

export default function AbaAcompanhamento({
  processo, dadosProcesso, antecedentes, antecedentesTotal, antecedentesPagina, antecedentesTamanhoPagina,
  antecedentesOrdenacao, antecedentesSelecionados, tipoConsultaAntecedentes, onAlternarTipoConsultaAntecedentes,
  onChangeOrdenacaoAntecedentes, onChangePaginaAntecedentes,
  onChangeTamanhoPaginaAntecedentes, onSelecionarAntecedente, onSelecionarTodosAntecedentes, fasesIds,
  idFaseProcesso, clientePermiteUnificar, podeModificarFaseUnificarJuntar, podeModificarFase,
  processoComTitulosLiberados, cobrancaJudicial, onAcordoGerado, onAntecedenteMutado, titulos,
  titulosTotal, titulosPagina, titulosTamanhoPagina, titulosOrdenacao,
  onChangeOrdenacaoTitulos, onChangePaginaTitulos, onChangeTamanhoPaginaTitulos,
  cannotEnabledTitulos, estadoDoDevedor,
  tipoTituloOptions, statusTituloOptions, motivosBaixaOptions, motivosExclusaoOptions, onTituloMutado,
  todosTitulosBaixados,
}) {
  const [simulacao, setSimulacao] = useState({ aberto: false, texto: '' });
  const [carregandoSimulacao, setCarregandoSimulacao] = useState(false);
  const [erroSimulacao, setErroSimulacao] = useState('');

  // Achado em code review (mesma classe de bug do "Demonstrativo de
  // Valores", Etapa 7): o guard `idProcessoRef` no `finally` de
  // `onClickSimularQuitacao` existe para não aplicar o resultado de uma
  // chamada do processo ANTIGO — mas isso inclui `setCarregandoSimulacao(false)`.
  // Trocar de processo com a simulação em voo travaria `carregandoSimulacao`
  // em `true` para sempre (nada mais o desliga), desabilitando "Simular
  // Quitação" permanentemente. Mesma correção: reset incondicional num
  // efeito keyed por `idProcesso`.
  useEffect(() => {
    setCarregandoSimulacao(false);
    setErroSimulacao('');
  }, [processo?.id]);

  // "Latest ref" — mutado direto no corpo (não em efeito), mesmo padrão de
  // BarraAcoesProcesso.jsx/aoSucesso: se o usuário trocar de processo antes
  // da resposta chegar, o resultado da simulação do processo antigo não deve
  // sobrescrever a tela do processo novo.
  const idProcessoRef = useRef(processo?.id);
  idProcessoRef.current = processo?.id;

  // Dialogs "Novo/Editar Título" e "Excluir Título" (AW2-1034 Etapas 4/5/6)
  // — `dialogTitulo` guarda um único estado consolidado (`aberto`/`titulo`/
  // `isEditar`) para o mesmo `DialogTitulo.jsx` reaproveitado nos 2 modos
  // (Novo: `titulo: null, isEditar: false`; Editar: `titulo` da linha
  // clicada, `isEditar: true`) — mesmo padrão de objeto único já usado acima
  // para `simulacao`/`resumoAntecedente`, em vez de 2 states booleanos
  // soltos que poderiam divergir entre si. `tituloParaExcluir` continua
  // separado (dialog distinto, `DialogExcluirTitulo.jsx`). Os 3 dialogs
  // ficam sempre montados, `open` alterna, mesmo padrão de
  // `DialogSimularQuitacao`/`DialogResumoAntecedente` acima (permite a
  // transição de saída do MUI `Dialog` animar). Reset ao trocar de processo
  // — mesma regra já documentada no CLAUDE.md do módulo ("estado de UI...
  // também precisa resetar ao trocar idProcesso", AW2-912): sem isso, trocar
  // de processo com um dos dialogs aberto deixaria o formulário/confirmação
  // do título do processo ANTERIOR visível sobre a tela do processo novo.
  const [dialogTitulo, setDialogTitulo] = useState(ESTADO_INICIAL_DIALOG_TITULO);
  const [tituloParaExcluir, setTituloParaExcluir] = useState(null);

  // "Expandir Títulos" (fullScreen, somente leitura) — estado puramente
  // local, mesmo padrão de `abrirExpandirHistorico`/`AbaHistorico.jsx`: sem
  // reset por `idProcesso` (o dialog não tem fetch/estado próprio, só
  // reflete `titulos` já gerenciado pelo container).
  const [abrirExpandirTitulos, setAbrirExpandirTitulos] = useState(false);

  useEffect(() => {
    setDialogTitulo(ESTADO_INICIAL_DIALOG_TITULO);
    setTituloParaExcluir(null);
  }, [processo?.id]);

  const onAbrirEditarTitulo = (titulo) => {
    setDialogTitulo({ aberto: true, titulo, isEditar: true });
  };

  const onAbrirNovoTitulo = () => {
    setDialogTitulo({ aberto: true, titulo: null, isEditar: false });
  };

  const onFecharDialogTitulo = () => {
    setDialogTitulo(atual => ({ ...atual, aberto: false }));
  };

  const disabledGerenciarTitulos = !hasUserPermissions(PERMISSAO_GERENCIAR_TITULOS);

  // Gate "Demonstrativo de Valores" (AW2-1034 Etapa 7) — réplica do gate
  // combinado decidido na sessão de arquitetura: `(disabledGerenciarTitulos
  // && !podeGerarRelatorio) || todosTitulosBaixados ||
  // processoComTitulosLiberados === false`. `podeGerarRelatorio` replica
  // `Titulos.jsx` real (`fasesPermitidasUsuario.includes(String(idFaseProcesso))`,
  // `state.authentication.userInformation.fases`) — mesmo padrão de
  // `hasUserPermissions` acima: leitura direta do Redux, recalculada a cada
  // render (sem `useMemo`), consistente com o resto do módulo.
  const fasesPermitidasUsuario = useSelector(state => state.authentication.userInformation.fases);
  const podeGerarRelatorio = Boolean(fasesPermitidasUsuario?.includes(String(idFaseProcesso)));
  const desabilitarDemonstrativoValores = (disabledGerenciarTitulos && !podeGerarRelatorio)
    || todosTitulosBaixados
    || processoComTitulosLiberados === false;

  const [carregandoDemonstrativo, setCarregandoDemonstrativo] = useState(false);
  const [erroDemonstrativo, setErroDemonstrativo] = useState('');

  // Achado em code review: o guard `idProcessoRef` acima existe para NÃO
  // aplicar o resultado de uma chamada do processo ANTIGO — mas isso inclui
  // o `setCarregandoDemonstrativo(false)` do `finally` abaixo. Trocar de
  // processo com a geração do PDF em voo travaria `carregandoDemonstrativo`
  // em `true` para sempre (nenhum outro código o desliga), desabilitando o
  // botão permanentemente para o processo novo e qualquer processo seguinte.
  // Mesma classe de bug já documentada no CLAUDE.md do módulo ("o guard
  // idProcessoRef bloqueia o finally de uma chamada antiga de limpar
  // carregando/erro para o processo novo — sempre resetar os dois num
  // efeito keyed por idProcesso também").
  useEffect(() => {
    setCarregandoDemonstrativo(false);
    setErroDemonstrativo('');
  }, [processo?.id]);

  const onClickDemonstrativoValores = async () => {
    const idProcessoAlvo = processo.id;
    try {
      setCarregandoDemonstrativo(true);
      const { data } = await imprimirMemoriaDeCalculo(idProcessoAlvo);
      if (idProcessoRef.current === idProcessoAlvo && data?.size > 0) {
        openPagePDF(data);
      }
    } catch {
      // `imprimirMemoriaDeCalculo` usa `responseType: 'blob'` — numa
      // resposta de erro, `erro.response.data` também vem como Blob, não o
      // JSON `{ message }` esperado; tentar `erro.response?.data?.message`
      // sempre resolveria `undefined` aqui. Mesmo padrão já usado por
      // `DialogMemoriaDeCalculo.jsx` (protótipo, mesma categoria de chamada
      // blob): mensagem genérica fixa, sem tentar extrair do backend.
      if (idProcessoRef.current === idProcessoAlvo) {
        setErroDemonstrativo(MENSAGEM_ERRO_DEMONSTRATIVO_VALORES);
      }
    } finally {
      if (idProcessoRef.current === idProcessoAlvo) {
        setCarregandoDemonstrativo(false);
      }
    }
  };

  const onClickSimularQuitacao = async () => {
    const idProcessoAlvo = processo.id;
    try {
      setCarregandoSimulacao(true);
      const { data } = await simularQuitacaoService(idProcessoAlvo);
      if (idProcessoRef.current === idProcessoAlvo) {
        setSimulacao({ aberto: true, texto: data || '' });
      }
    } catch (erro) {
      if (idProcessoRef.current === idProcessoAlvo) {
        setErroSimulacao(erro.response?.data?.message || MENSAGEM_ERRO_SIMULAR_QUITACAO);
      }
    } finally {
      if (idProcessoRef.current === idProcessoAlvo) {
        setCarregandoSimulacao(false);
      }
    }
  };

  // Dialog "Resumo" da grid de Antecedentes (AW2-1033 Etapa 5) — mesmo
  // padrão de `DialogSimularQuitacao` acima: o fetch acontece aqui (no
  // clique do botão), não dentro do dialog (`DialogResumoAntecedente.jsx` é
  // um componente burro, só exibe `carregando`/`erro`/`dados` já
  // resolvidos). `numeroAntecedenteRef` guarda qual antecedente foi
  // clicado por último — clicar em "Resumo" de uma linha diferente antes da
  // resposta anterior chegar não pode deixar a resposta antiga sobrescrever
  // o dialog com o dado do antecedente errado.
  const [resumoAntecedente, setResumoAntecedente] = useState({
    aberto: false, numero: null, carregando: false, erro: '', dados: null,
  });
  const numeroAntecedenteRef = useRef(null);

  const onAbrirResumoAntecedente = (numeroAntecedente) => {
    numeroAntecedenteRef.current = numeroAntecedente;
    setResumoAntecedente({
      aberto: true, numero: numeroAntecedente, carregando: true, erro: '', dados: null,
    });

    buscarAcompanhamentoDoProcessoService(numeroAntecedente)
      .then(({ data }) => {
        if (numeroAntecedenteRef.current === numeroAntecedente) {
          setResumoAntecedente({
            aberto: true, numero: numeroAntecedente, carregando: false, erro: '', dados: data,
          });
        }
      })
      .catch((erro) => {
        if (numeroAntecedenteRef.current === numeroAntecedente) {
          setResumoAntecedente({
            aberto: true,
            numero: numeroAntecedente,
            carregando: false,
            erro: erro.response?.data?.message || MENSAGEM_ERRO_RESUMO_ANTECEDENTE,
            dados: null,
          });
        }
      });
  };

  const onCloseResumoAntecedente = () => {
    // Invalida também `numeroAntecedenteRef` (não só `aberto`) — sem isso,
    // uma resposta do fetch chegando DEPOIS do usuário fechar o dialog ainda
    // passaria no guard (`numeroAntecedenteRef.current === numeroAntecedente`
    // continuaria batendo) e reabriria o dialog sozinho via `aberto: true`
    // hardcoded no `.then`/`.catch`.
    numeroAntecedenteRef.current = null;
    setResumoAntecedente(atual => ({ ...atual, aberto: false }));
  };

  // Achado testando ao vivo no browser: gatear a grid E os ícones
  // Unificar/Juntar só por `antecedentes.length > 0` prende o usuário sem
  // saída assim que uma ação filtra para 0 antecedentes elegíveis (ex.:
  // nenhum antecedente elegível para Unificar) — os ícones, que fazem parte
  // do mesmo `ResumoAntecedenteAtual`, desapareceriam junto, sem forma de
  // voltar para `SIMPLES`. Mesma lógica que a tela real usa
  // (`antecedentes.content.length || actionButtonType.hasType()` em
  // `AntecedentesProcessos.jsx`): mostra a grid (mesmo vazia) e os ícones
  // sempre que HOUVER dado OU uma ação já estiver escolhida — só cai no
  // estado "Não foram encontrados..." quando as duas condições faltam.
  const mostrarGridAntecedentes = antecedentes != null
    && (antecedentes.length > 0 || tipoConsultaAntecedentes !== tiposConsultasAntecedentesTypes.simples);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.8vw, 22px)' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.85fr) minmax(0, 1.15fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          alignItems: 'start',
        }}
      >
        {/* --- Cálculo do Processo --- */}
        <CardWrapper>
          <SectionTitle>Cálculo do Processo</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {LINHAS_CALCULO.map(l => (
              <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    width: '38%',
                    flexShrink: 0,
                    fontSize: 'clamp(12px, 0.7vw, 16px)',
                    fontWeight: l.destaque ? 700 : 400,
                    color: C.black,
                  }}
                >
                  {l.label}
                </Typography>
                <Field
                  tipo="texto"
                  value={dadosProcesso?.[l.id] == null ? '—' : formatBRL(dadosProcesso[l.id])}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
                <Typography variant="caption" sx={{ width: 22, color: C.warmGrey, flexShrink: 0 }}>
                  {l.sinal}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2,
            }}
          >
            <BotaoNovoAcordo
              idProcesso={processo?.id}
              podeModificarFase={podeModificarFase}
              processoComTitulosLiberados={processoComTitulosLiberados}
              nomeUsuario={dadosProcesso?.nomeUsuario}
              nomeFase={dadosProcesso?.nomeFase}
              cobrancaJudicial={cobrancaJudicial}
              onAcordoGerado={onAcordoGerado}
            />
            <ButtonOutlined onClick={onClickSimularQuitacao} disabled={!processo || carregandoSimulacao}>
              SIMULAR QUITAÇÃO
            </ButtonOutlined>
          </Box>
        </CardWrapper>

        {/* --- Processos Antecedentes --- */}
        <CardWrapper>
          <SectionTitle>
            Processos Antecedentes
          </SectionTitle>
          {mostrarGridAntecedentes ? (
            <Box sx={{ overflowX: 'auto' }}>
              <TableAntecedentesProcesso
                antecedentes={antecedentes}
                ordenacao={antecedentesOrdenacao}
                onChangeOrdenacao={onChangeOrdenacaoAntecedentes}
                selecionados={antecedentesSelecionados}
                onSelecionar={onSelecionarAntecedente}
                onSelecionarTodos={onSelecionarTodosAntecedentes}
                idFaseBaixa={fasesIds?.idFaseBaixa}
                onAbrirResumo={onAbrirResumoAntecedente}
                tipoConsultaAntecedentes={tipoConsultaAntecedentes}
              />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 3 }}>
              {antecedentes == null ? 'Carregando...' : 'Não foram encontrados Antecedentes para este Processo.'}
            </Typography>
          )}
          {/*
            Visibilidade decidida por `antecedentesTotal`, não por
            `antecedentes.length` — uma página fora do intervalo válido pode
            responder com `content` vazio mas total > 0 (achado em code
            review); nesse caso os controles de paginação continuam visíveis
            para o usuário conseguir voltar para uma página válida, mesmo com
            a área da tabela mostrando a mensagem de "vazio" acima. O rótulo
            nativo ("X–Y de Z") já mostra a contagem real (Etapa 2),
            substituindo o "Exibindo: N" provisório da Etapa 1.
          */}
          {antecedentesTotal > 0 && (
            <TablePagination
              component="div"
              count={antecedentesTotal}
              page={antecedentesPagina}
              rowsPerPage={antecedentesTamanhoPagina}
              rowsPerPageOptions={OPCOES_TAMANHO_PAGINA_ANTECEDENTES}
              onPageChange={(event, novaPagina) => onChangePaginaAntecedentes(novaPagina)}
              onRowsPerPageChange={onChangeTamanhoPaginaAntecedentes}
              labelRowsPerPage="Linhas por página:"
            />
          )}
          {mostrarGridAntecedentes && (
            <ResumoAntecedenteAtual
              idProcesso={processo?.id}
              dadosProcesso={dadosProcesso}
              idFaseProcesso={idFaseProcesso}
              podeModificarFase={podeModificarFaseUnificarJuntar}
              clientePermiteUnificar={clientePermiteUnificar}
              fasesIds={fasesIds}
              idsAntecedentesSelecionados={TableUtils.getIdsSelected(antecedentesSelecionados)}
              onSucesso={onAntecedenteMutado}
              tipoConsultaAntecedentes={tipoConsultaAntecedentes}
              onAlternarTipo={onAlternarTipoConsultaAntecedentes}
            />
          )}
        </CardWrapper>
      </Box>

      {/* --- Títulos do processo --- */}
      <CardWrapper>
        <SectionTitle
          right={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ButtonOutlined
                size="small"
                onClick={onAbrirNovoTitulo}
                disabled={disabledGerenciarTitulos || cannotEnabledTitulos}
              >
                NOVO
              </ButtonOutlined>
              <ButtonOutlined
                size="small"
                onClick={onClickDemonstrativoValores}
                disabled={!processo || carregandoDemonstrativo || desabilitarDemonstrativoValores}
              >
                DEMONSTRATIVO DE VALORES
              </ButtonOutlined>
              <ButtonOutlined size="small" onClick={() => setAbrirExpandirTitulos(true)}>
                EXPANDIR TÍTULOS
              </ButtonOutlined>
            </Box>
          )}
        >
          Títulos do processo
        </SectionTitle>
        {titulos?.length ? (
          <Box sx={{ overflowX: 'auto' }}>
            <TableTitulosProcesso
              titulos={titulos}
              ordenacao={titulosOrdenacao}
              onChangeOrdenacao={onChangeOrdenacaoTitulos}
              disabledGerenciarTitulos={disabledGerenciarTitulos}
              cannotEnabled={cannotEnabledTitulos}
              onEditarTitulo={onAbrirEditarTitulo}
              onExcluirTitulo={setTituloParaExcluir}
            />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 3 }}>
            {titulos == null ? 'Carregando...' : 'Não foram encontrados Títulos para este Processo.'}
          </Typography>
        )}
        {/*
          Mesmo cuidado já documentado para Antecedentes: visibilidade por
          `titulosTotal`, não por `titulos.length` — uma página fora do
          intervalo válido pode responder com `content` vazio mas total > 0.
        */}
        {titulosTotal > 0 && (
          <TablePagination
            component="div"
            count={titulosTotal}
            page={titulosPagina}
            rowsPerPage={titulosTamanhoPagina}
            rowsPerPageOptions={OPCOES_TAMANHO_PAGINA_TITULOS}
            onPageChange={(event, novaPagina) => onChangePaginaTitulos(novaPagina)}
            onRowsPerPageChange={onChangeTamanhoPaginaTitulos}
            labelRowsPerPage="Linhas por página:"
          />
        )}
      </CardWrapper>

      <DialogSimularQuitacao
        open={simulacao.aberto}
        texto={simulacao.texto}
        onClose={() => setSimulacao(atual => ({ ...atual, aberto: false }))}
      />
      <DialogResumoAntecedente
        open={resumoAntecedente.aberto}
        numeroAntecedente={resumoAntecedente.numero}
        carregando={resumoAntecedente.carregando}
        erro={resumoAntecedente.erro}
        dados={resumoAntecedente.dados}
        onClose={onCloseResumoAntecedente}
      />
      <DialogTitulo
        open={dialogTitulo.aberto}
        titulo={dialogTitulo.titulo}
        isEditar={dialogTitulo.isEditar}
        idProcesso={processo?.id}
        estadoDoDevedor={estadoDoDevedor}
        tipoTituloOptions={tipoTituloOptions}
        statusTituloOptions={statusTituloOptions}
        motivosBaixaOptions={motivosBaixaOptions}
        onClose={onFecharDialogTitulo}
        onSucesso={onTituloMutado}
      />
      <DialogExcluirTitulo
        open={Boolean(tituloParaExcluir)}
        titulo={tituloParaExcluir}
        idProcesso={processo?.id}
        motivosExclusaoOptions={motivosExclusaoOptions}
        onClose={() => setTituloParaExcluir(null)}
        onSucesso={onTituloMutado}
      />
      <DialogExpandirTitulos
        open={abrirExpandirTitulos}
        onClose={() => setAbrirExpandirTitulos(false)}
        titulos={titulos}
        titulosTotal={titulosTotal}
        titulosPagina={titulosPagina}
        titulosTamanhoPagina={titulosTamanhoPagina}
        titulosOrdenacao={titulosOrdenacao}
        onChangeOrdenacaoTitulos={onChangeOrdenacaoTitulos}
        onChangePaginaTitulos={onChangePaginaTitulos}
        onChangeTamanhoPaginaTitulos={onChangeTamanhoPaginaTitulos}
      />
      <Snackbar
        open={Boolean(erroSimulacao)}
        autoHideDuration={4000}
        onClose={() => setErroSimulacao('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErroSimulacao('')}>
          {erroSimulacao}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(erroDemonstrativo)}
        autoHideDuration={4000}
        onClose={() => setErroDemonstrativo('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErroDemonstrativo('')}>
          {erroDemonstrativo}
        </Alert>
      </Snackbar>
    </Box>
  );
}
AbaAcompanhamento.propTypes = {
  processo: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
  dadosProcesso: PropTypes.object,
  antecedentes: PropTypes.array,
  antecedentesTotal: PropTypes.number,
  antecedentesPagina: PropTypes.number,
  antecedentesTamanhoPagina: PropTypes.number,
  antecedentesOrdenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  antecedentesSelecionados: PropTypes.instanceOf(Map),
  tipoConsultaAntecedentes: PropTypes.string,
  onAlternarTipoConsultaAntecedentes: PropTypes.func,
  onChangeOrdenacaoAntecedentes: PropTypes.func,
  onChangePaginaAntecedentes: PropTypes.func,
  onChangeTamanhoPaginaAntecedentes: PropTypes.func,
  onSelecionarAntecedente: PropTypes.func,
  onSelecionarTodosAntecedentes: PropTypes.func,
  fasesIds: PropTypes.shape({
    idFaseAcordoSeguro: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idFaseBaixa: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idFaseAcordoSeguroExterno: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idFaseProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  clientePermiteUnificar: PropTypes.bool,
  podeModificarFaseUnificarJuntar: PropTypes.bool,
  podeModificarFase: PropTypes.bool,
  processoComTitulosLiberados: PropTypes.bool,
  cobrancaJudicial: PropTypes.bool,
  onAcordoGerado: PropTypes.func,
  onAntecedenteMutado: PropTypes.func,
  titulos: PropTypes.array,
  titulosTotal: PropTypes.number,
  titulosPagina: PropTypes.number,
  titulosTamanhoPagina: PropTypes.number,
  titulosOrdenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  onChangeOrdenacaoTitulos: PropTypes.func,
  onChangePaginaTitulos: PropTypes.func,
  onChangeTamanhoPaginaTitulos: PropTypes.func,
  cannotEnabledTitulos: PropTypes.bool,
  estadoDoDevedor: PropTypes.string,
  tipoTituloOptions: PropTypes.array,
  statusTituloOptions: PropTypes.array,
  motivosBaixaOptions: PropTypes.array,
  motivosExclusaoOptions: PropTypes.array,
  // Sem default — é o único caminho para fechar `DialogTitulo`/`DialogExcluirTitulo`
  // com sucesso e atualizar a grid/"Cálculo do Processo"; um consumidor que
  // esquecer de passá-lo deve quebrar em dev, não virar um "Confirmar"
  // silenciosamente morto (mesmo critério já usado para
  // `onSucesso`/`onChangePesquisaAtiva` documentado no CLAUDE.md do módulo).
  onTituloMutado: PropTypes.func.isRequired,
  todosTitulosBaixados: PropTypes.bool,
};
AbaAcompanhamento.defaultProps = {
  processo: null,
  dadosProcesso: null,
  antecedentes: null,
  antecedentesTotal: 0,
  antecedentesPagina: 0,
  antecedentesTamanhoPagina: 10,
  antecedentesOrdenacao: { campo: 'idProcesso', direcao: 'ASC' },
  antecedentesSelecionados: new Map(),
  tipoConsultaAntecedentes: tiposConsultasAntecedentesTypes.simples,
  onAlternarTipoConsultaAntecedentes: null,
  onChangeOrdenacaoAntecedentes: null,
  onChangePaginaAntecedentes: null,
  onChangeTamanhoPaginaAntecedentes: null,
  onSelecionarAntecedente: null,
  onSelecionarTodosAntecedentes: null,
  fasesIds: null,
  idFaseProcesso: null,
  clientePermiteUnificar: false,
  podeModificarFaseUnificarJuntar: false,
  podeModificarFase: false,
  processoComTitulosLiberados: null,
  cobrancaJudicial: false,
  onAcordoGerado: null,
  onAntecedenteMutado: null,
  titulos: null,
  titulosTotal: 0,
  titulosPagina: 0,
  titulosTamanhoPagina: 10,
  titulosOrdenacao: { campo: 'dataVencimento', direcao: 'ASC' },
  onChangeOrdenacaoTitulos: null,
  onChangePaginaTitulos: null,
  onChangeTamanhoPaginaTitulos: null,
  cannotEnabledTitulos: true,
  estadoDoDevedor: null,
  tipoTituloOptions: [],
  statusTituloOptions: [],
  motivosBaixaOptions: [],
  motivosExclusaoOptions: [],
  todosTitulosBaixados: false,
};
